/**
 * Live public booking page preview (right-side panel or inline card).
 * Display-only — reads from BusinessSignupData, writes nothing.
 */
import * as React from 'react';
import { V2C, fontBody, fontDisplay } from './tokens';
import type { BusinessSignupData } from '@/components/onboarding/magic-steps/BusinessSignup';

interface Props {
  formData: BusinessSignupData;
}

function getInitials(name: string): string {
  return (name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('');
}

export default function V2PublicPreview({ formData }: Props) {
  const { businessName, title, bio, logoPreview, bookingUrl, services, fullName, logoEmoji } = formData;
  const initials = getInitials(fullName || businessName || '');

  return (
    <div style={{
      background: '#fff', borderRadius: 20, border: `1px solid ${V2C.line}`,
      overflow: 'hidden', boxShadow: '0 4px 24px rgba(89,86,246,0.08)',
    }}>
      {/* Browser chrome */}
      <div style={{
        background: V2C.bgAlt, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 6,
        borderBottom: `1px solid ${V2C.line}`,
      }}>
        <div style={{ display: 'flex', gap: 5 }}>
          {['#FF5F56', '#FFBD2E', '#27C93F'].map(c => (
            <div key={c} style={{ width: 8, height: 8, borderRadius: 4, background: c }} />
          ))}
        </div>
        <div style={{
          flex: 1, margin: '0 8px', background: '#fff', borderRadius: 6,
          padding: '3px 10px', fontSize: 10, color: V2C.mute, fontFamily: fontBody,
          border: `1px solid ${V2C.line}`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          getfiggy.link/{bookingUrl || 'your-handle'}
        </div>
      </div>

      {/* Page content */}
      <div style={{ padding: '20px 16px' }}>
        {/* Logo + name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, flexShrink: 0,
            background: logoPreview ? 'transparent' : V2C.brandSoft,
            overflow: 'hidden', border: `1px solid ${V2C.line}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {logoPreview
              ? <img src={logoPreview} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : logoEmoji
                ? <span style={{ fontSize: 18 }}>{logoEmoji}</span>
                : initials
                  ? <span style={{ fontSize: 14, fontWeight: 700, color: V2C.brand, fontFamily: fontBody }}>{initials}</span>
                  : <span style={{ fontSize: 18 }}>✨</span>
            }
          </div>
          <div>
            <div style={{ fontFamily: fontDisplay, fontWeight: 500, fontSize: 15, color: V2C.ink, lineHeight: 1.2 }}>
              {businessName || 'Your Business'}
            </div>
            {title && (
              <div style={{ fontSize: 11.5, color: V2C.mute, fontFamily: fontBody, marginTop: 2 }}>
                {title}
              </div>
            )}
          </div>
        </div>

        {bio && (
          <p style={{
            fontSize: 12, color: V2C.mute, fontFamily: fontBody,
            lineHeight: 1.5, margin: '0 0 12px',
          }}>
            {bio.length > 100 ? bio.slice(0, 100) + '…' : bio}
          </p>
        )}

        {/* Services preview */}
        {(services ?? []).length > 0 && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: V2C.mute, letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 6, fontFamily: fontBody }}>
              Services
            </div>
            {(services as { id: string; name: string; duration: number; price: number }[]).slice(0, 3).map(svc => (
              <div key={svc.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '7px 10px', background: V2C.bgAlt, borderRadius: 8, marginBottom: 5,
                border: `1px solid ${V2C.line}`,
              }}>
                <div>
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: V2C.ink, fontFamily: fontBody }}>{svc.name}</div>
                  <div style={{ fontSize: 10, color: V2C.mute, fontFamily: fontBody }}>
                    {svc.duration >= 60 ? `${svc.duration / 60}h` : `${svc.duration}m`}
                  </div>
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: V2C.brand, fontFamily: fontBody }}>${svc.price}</div>
              </div>
            ))}
            {services!.length > 3 && (
              <div style={{ fontSize: 10, color: V2C.mute, textAlign: 'center', fontFamily: fontBody }}>
                +{services!.length - 3} more
              </div>
            )}
          </div>
        )}

        {/* Book button placeholder */}
        <div style={{
          marginTop: 14, background: V2C.brand, borderRadius: 10,
          padding: '9px', textAlign: 'center',
          fontSize: 12, fontWeight: 700, color: '#fff', fontFamily: fontBody,
        }}>
          Book an appointment
        </div>
      </div>
    </div>
  );
}
